import { AsyncButton } from "aic";
import { IRequestStatus } from "functions/api/interface";
import { ReactNode, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";

interface AreYouSureModal {
  id: string;
  show: boolean;
  title?: string;
  message: string | ReactNode;
  onOk: () => void;
  onClose?: () => void;
  requestStatus?: IRequestStatus<unknown>;
}

/**
 * Confirmation modal dialog with OK and Close buttons.
 *
 * The modal will automatically close when requestStatus transitions from requesting=true to requesting=false.
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the modal
 * @param props.show - Whether the modal is currently displayed
 * @param props.title - Optional modal title, defaults to "Are you sure?"
 * @param props.message - Message to display in the modal body
 * @param props.onOk - Callback invoked when OK button is clicked
 * @param props.onClose - Optional callback invoked when modal is closed
 * @param props.requestStatus - Optional request status object for loading and error states on the OK button
 */
export const AreYouSureModal = ({
  id,
  show,
  title = "Are you sure?",
  message,
  onOk,
  onClose,
  requestStatus,
}: AreYouSureModal): React.ReactElement => {
  // Track previous requesting state
  const prevRequesting = useRef(false);
  const requesting = requestStatus?.requesting ?? false;

  /**
   * Closes the modal automatically when the async request completes.
   * Detects when requestStatus transitions from requesting=true to requesting=false,
   * and calls onClose to close the modal. This ensures the spinner is visible during
   * the async operation and the modal closes only after completion.
   *
   * @param requesting - Current request status (true if async operation is in progress)
   * @param onClose - Callback to close the modal
   */
  useEffect(() => {
    // If previously requesting and now not requesting, close modal
    if (prevRequesting.current && !requesting) {
      onClose?.();
    }
    prevRequesting.current = requesting;
  }, [requesting, onClose]);

  return (
    <Modal
      id={id}
      show={show}
      onHide={onClose}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{typeof message === "string" ? <p>{message}</p> : message}</Modal.Body>
      <Modal.Footer>
        <AsyncButton
          id={`${id}-ok`}
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOk();
          }}
          requestStatus={requestStatus}
        >
          OK
        </AsyncButton>
        <AsyncButton
          id={`${id}-close`}
          variant="secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose?.();
          }}
        >
          Close
        </AsyncButton>
      </Modal.Footer>
    </Modal>
  );
};

AreYouSureModal.displayName = "AreYouSureModal";
